import { useState, FormEvent } from 'react';

export const ORDER_FORM_URL = 'https://readdy.ai/api/form/d7iei68i2k4pvivir1jg';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface CanEntry {
  id: string;
  type: 'regular' | 'trash';
  logoStyle: string;
  leftNumber: string;
  rightNumber: string;
}

export interface PouchEntry {
  id: string;
  brand: string;
  flavor: string;
  strength: string;
  quantity: number;
}

export interface OrderData {
  name: string;
  phone: string;
  regularCans: CanEntry[];
  trashCans: CanEntry[];
  pouchEntries: PouchEntry[];
  logistics: string;
  specialRequests: string;
  totalAmount: number;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const REGULAR_CAN_PRICE = 50;
const TRASH_CAN_PRICE = 75;
const POUCH_PRICE = 7.5;

const brands = ['ZYN', 'VELO', 'ALPS'];
const strengths = ['3MG', '6MG', '9MG'];
const numberOptions = ['none', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const uid = () => Math.random().toString(36).slice(2);

const newCanEntry = (type: 'regular' | 'trash'): CanEntry => ({
  id: uid(), type, logoStyle: '', leftNumber: 'none', rightNumber: 'none',
});

const newPouchEntry = (): PouchEntry => ({
  id: uid(), brand: '', flavor: '', strength: '', quantity: 1,
});

// ─── Style helpers ───────────────────────────────────────────────────────────

const inputClass =
  'w-full bg-[#141414] border border-[#3a3a3a] text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#CE1126] transition-colors duration-200 placeholder-[#555]';

const labelClass = 'block font-mono text-xs tracking-[0.25em] uppercase text-[#aaa] mb-2';

// ─── Sub-components ───────────────────────────────────────────────────────────

const RequiredStar = () => (
  <span className="text-[#D4FF00] ml-1" aria-label="required">*</span>
);

const InfoTooltip = ({ text }: { text: string }) => {
  const [visible, setVisible] = useState(false);
  return (
    <span className="relative inline-flex items-center ml-2">
      <button
        type="button"
        className="w-4 h-4 flex items-center justify-center rounded-full border border-[#555] text-[#888] hover:border-[#D4FF00] hover:text-[#D4FF00] transition-colors duration-150 cursor-pointer"
        style={{ fontSize: '9px', lineHeight: 1 }}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
        aria-label="More info"
      >
        i
      </button>
      {visible && (
        <span
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-[#1a1a1a] border border-[#D4FF00]/40 text-[#ccc] font-mono text-xs px-3 py-2 z-50 pointer-events-none"
          style={{ animation: 'fadeSlideIn 0.15s ease' }}
        >
          {text}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#D4FF00]/40" />
        </span>
      )}
    </span>
  );
};

// ─── Can Entry Card ───────────────────────────────────────────────────────────

interface CanEntryCardProps {
  entry: CanEntry;
  index: number;
  canRemove: boolean;
  onChange: (id: string, field: keyof CanEntry, value: string) => void;
  onRemove: (id: string) => void;
}

const CanEntryCard = ({ entry, index, canRemove, onChange, onRemove }: CanEntryCardProps) => {
  const accentColor = entry.type === 'regular' ? '#CE1126' : '#C0C0C0';
  const label = entry.type === 'regular' ? 'Regular Can' : 'Trash Can';
  const price = entry.type === 'regular' ? REGULAR_CAN_PRICE : TRASH_CAN_PRICE;

  return (
    <div
      className="border bg-[#0f0f0f] p-5 space-y-5 relative"
      style={{ borderColor: `${accentColor}30`, animation: 'fadeSlideIn 0.25s ease' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span
            className="font-mono text-xs tracking-[0.3em] uppercase"
            style={{ color: accentColor }}
          >
            {label} #{index + 1}
          </span>
          <span className="font-mono text-xs text-[#555]">${price.toFixed(2)}</span>
        </div>
        {canRemove && (
          <button
            type="button"
            onClick={() => onRemove(entry.id)}
            className="w-7 h-7 flex items-center justify-center border border-[#3a3a3a] text-[#666] hover:border-[#CE1126] hover:text-[#CE1126] transition-all duration-150 cursor-pointer"
            aria-label="Remove this can"
          >
            <i className="ri-close-line text-sm" />
          </button>
        )}
      </div>

      {/* Logo Style */}
      <div>
        <label className={labelClass}>Custom Logo Theme</label>
        <div className="flex flex-col sm:flex-row gap-3">
          {[
            {
              key: 'giants',
              src: 'https://storage.readdy-site.link/project_files/ebc6b099-e7ee-4861-be92-8c372e9c9635/e853ece0-dd76-45b6-a429-a0eb1841dc3d_giants_black-cropped.svg?v=185ddb00da9f18186b7d51aa73eb960e',
              alt: 'Giants logo',
            },
            {
              key: '49ers',
              src: 'https://storage.readdy-site.link/project_files/ebc6b099-e7ee-4861-be92-8c372e9c9635/037d32fa-ad5c-4316-8727-e8396d483df1_49s_black-cropped.svg?v=e6daadf7b16041736070485e4f2cc78a',
              alt: '49ers logo',
            },
          ].map((logo) => (
            <button
              key={logo.key}
              type="button"
              onClick={() => onChange(entry.id, 'logoStyle', logo.key)}
              className={`flex-1 py-4 border-2 transition-all duration-200 cursor-pointer flex flex-col items-center justify-center gap-2 ${
                entry.logoStyle === logo.key
                  ? 'border-[#D4FF00]'
                  : 'border-[#3a3a3a] hover:border-[#555]'
              }`}
              style={{ background: '#141414' }}
            >
              <div className="w-20 h-20 flex items-center justify-center">
                <img
                  src={logo.src}
                  alt={logo.alt}
                  className="w-full h-full object-contain"
                  style={{ filter: 'brightness(0) invert(1)' }}
                />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Numbers */}
      <div>
        <label className={`${labelClass} flex items-center`}>
          Station Number to Engrave
          <InfoTooltip text="Numbers appear as shown in the team logos — the left and right numbers are engraved on each respective side of the can lid." />
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(['left', 'right'] as const).map((side) => {
            const field = side === 'left' ? 'leftNumber' : 'rightNumber';
            const current = entry[field];
            return (
              <div key={side} className="border border-[#3a3a3a] bg-[#141414] p-3">
                <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#888] mb-2 flex items-center gap-2">
                  <i className={side === 'left' ? 'ri-arrow-left-s-line text-[#CE1126]' : 'ri-arrow-right-s-line text-[#CE1126]'} />
                  {side === 'left' ? 'Left' : 'Right'} Number
                </p>
                <div className="grid grid-cols-4 gap-1.5">
                  {numberOptions.map((n) => (
                    <button
                      key={`${entry.id}-${side}-${n}`}
                      type="button"
                      onClick={() => onChange(entry.id, field, n)}
                      className={`py-2.5 border font-mono text-sm tracking-widest transition-all duration-150 cursor-pointer whitespace-nowrap ${
                        current === n
                          ? 'bg-[#CE1126] border-[#CE1126] text-white'
                          : 'bg-[#1a1a1a] border-[#3a3a3a] text-[#aaa] hover:border-[#CE1126]/50'
                      }`}
                      style={{ fontFamily: n === 'none' ? 'inherit' : '"Black Ops One", "Impact", sans-serif' }}
                    >
                      {n === 'none' ? <span className="text-[9px] tracking-widest">NONE</span> : n}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── Pouch Entry Card ─────────────────────────────────────────────────────────

interface PouchEntryCardProps {
  entry: PouchEntry;
  index: number;
  canRemove: boolean;
  onChange: (id: string, field: keyof PouchEntry, value: string | number) => void;
  onRemove: (id: string) => void;
  error?: { brand?: string; strength?: string };
}

const PouchEntryCard = ({ entry, index, canRemove, onChange, onRemove, error }: PouchEntryCardProps) => (
  <div
    className="border border-[#D4FF00]/20 bg-[#0f0f0f] p-5 space-y-5 relative"
    style={{ animation: 'fadeSlideIn 0.25s ease' }}
  >
    <div className="flex items-center justify-between">
      <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#D4FF00]">
        Flavor #{index + 1}
      </p>
      {canRemove && (
        <button
          type="button"
          onClick={() => onRemove(entry.id)}
          className="w-7 h-7 flex items-center justify-center border border-[#3a3a3a] text-[#666] hover:border-[#CE1126] hover:text-[#CE1126] transition-all duration-150 cursor-pointer"
          aria-label="Remove this flavor"
        >
          <i className="ri-close-line text-sm" />
        </button>
      )}
    </div>

    <div>
      <label className={labelClass}>Brand<RequiredStar /></label>
      <div className="flex flex-wrap gap-3">
        {brands.map((b) => (
          <button
            key={b}
            type="button"
            onClick={() => onChange(entry.id, 'brand', b)}
            className={`w-24 py-3 border font-black text-sm tracking-widest transition-all duration-200 cursor-pointer whitespace-nowrap ${
              entry.brand === b
                ? 'bg-[#CE1126] border-[#CE1126] text-white'
                : 'bg-[#1a1a1a] border-[#3a3a3a] text-[#aaa] hover:border-[#CE1126]/60'
            }`}
            style={{ fontFamily: '"Black Ops One", "Impact", sans-serif' }}
          >
            {b}
          </button>
        ))}
      </div>
      {error?.brand && <p className="text-[#CE1126] font-mono text-xs mt-1">{error.brand}</p>}
    </div>

    <div>
      <label className={labelClass}>Flavor</label>
      <input
        type="text"
        className={inputClass}
        placeholder="Enter preferred flavor (optional)..."
        value={entry.flavor}
        onChange={(e) => onChange(entry.id, 'flavor', e.target.value)}
      />
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <label className={labelClass}>Nicotine Strength<RequiredStar /></label>
        <div className="flex gap-2">
          {strengths.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onChange(entry.id, 'strength', s)}
              className={`flex-1 py-3 border font-black text-sm tracking-widest transition-all duration-200 cursor-pointer whitespace-nowrap ${
                entry.strength === s
                  ? 'bg-[#CE1126] border-[#CE1126] text-white'
                  : 'bg-[#1a1a1a] border-[#3a3a3a] text-[#aaa] hover:border-[#CE1126]/60'
              }`}
              style={{ fontFamily: '"Black Ops One", "Impact", sans-serif' }}
            >
              {s}
            </button>
          ))}
        </div>
        {error?.strength && <p className="text-[#CE1126] font-mono text-xs mt-1">{error.strength}</p>}
      </div>

      <div>
        <label className={labelClass}>Packs</label>
        <div className="flex items-center border border-[#3a3a3a] bg-[#1a1a1a] overflow-hidden">
          <button
            type="button"
            onClick={() => onChange(entry.id, 'quantity', Math.max(1, entry.quantity - 1))}
            disabled={entry.quantity <= 1}
            className="w-12 h-14 flex items-center justify-center text-xl font-black text-[#aaa] border-r border-[#3a3a3a] transition-all duration-200 cursor-pointer hover:bg-[#CE1126]/20 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
          >
            −
          </button>
          <div className="flex-1 flex items-center justify-center h-14">
            <span
              className="text-white text-2xl font-black tracking-widest"
              style={{ fontFamily: '"Courier New", monospace' }}
            >
              {entry.quantity}
            </span>
          </div>
          <button
            type="button"
            onClick={() => onChange(entry.id, 'quantity', entry.quantity + 1)}
            className="w-12 h-14 flex items-center justify-center text-xl font-black text-[#aaa] border-l border-[#3a3a3a] transition-all duration-200 cursor-pointer hover:bg-[#CE1126]/20 hover:text-white"
          >
            +
          </button>
        </div>
        <p className="text-[#555] font-mono text-xs mt-1">
          $7.50 × {entry.quantity} = ${(entry.quantity * POUCH_PRICE).toFixed(2)}
        </p>
      </div>
    </div>
  </div>
);

// ─── Section Toggle Header ────────────────────────────────────────────────────

interface SectionToggleProps {
  active: boolean;
  onToggle: () => void;
  icon: string;
  label: string;
  price: string;
  accentColor: string;
  count?: number;
}

const SectionToggle = ({ active, onToggle, icon, label, price, accentColor, count }: SectionToggleProps) => (
  <button
    type="button"
    onClick={onToggle}
    className="w-full flex items-center gap-4 px-5 py-4 border-2 transition-all duration-200 cursor-pointer text-left"
    style={{
      background: active ? `${accentColor}10` : '#141414',
      borderColor: active ? accentColor : '#3a3a3a',
    }}
  >
    <span
      className="w-6 h-6 border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200"
      style={{
        borderColor: active ? accentColor : '#555',
        background: active ? accentColor : 'transparent',
      }}
    >
      {active && <i className="ri-check-line text-black text-xs" />}
    </span>
    <span className="w-8 h-8 flex items-center justify-center flex-shrink-0">
      <i className={`${icon} text-xl`} style={{ color: active ? accentColor : '#555' }} />
    </span>
    <span className="flex-1">
      <span
        className="block text-sm tracking-widest mb-0.5"
        style={{
          fontFamily: '"Black Ops One", "Impact", sans-serif',
          color: active ? '#fff' : '#aaa',
        }}
      >
        {label}
      </span>
      <span className="font-mono text-xs" style={{ color: active ? accentColor : '#555' }}>
        {price}
      </span>
    </span>
    {active && count !== undefined && count > 0 && (
      <span
        className="font-mono text-xs px-2 py-1 border"
        style={{ color: accentColor, borderColor: `${accentColor}50` }}
      >
        ×{count}
      </span>
    )}
  </button>
);

// ─── Main Form ────────────────────────────────────────────────────────────────

interface FormErrors {
  name?: string;
  phone?: string;
  atLeastOne?: string;
  logistics?: string;
  pouches?: Record<string, { brand?: string; strength?: string }>;
}

const OrderForm = ({ onSuccess }: { onSuccess: (data: OrderData) => void }) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [logistics, setLogistics] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');

  const [regularCans, setRegularCans] = useState<CanEntry[]>([]);
  const [trashCans, setTrashCans] = useState<CanEntry[]>([]);
  const [pouchEntries, setPouchEntries] = useState<PouchEntry[]>([]);

  const [regularOpen, setRegularOpen] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  const [pouchesOpen, setPouchesOpen] = useState(false);

  const [errors, setErrors] = useState<FormErrors>({});

  // ── Totals ──
  const regularTotal = regularCans.length * REGULAR_CAN_PRICE;
  const trashTotal = trashCans.length * TRASH_CAN_PRICE;
  const totalPouchPacks = pouchEntries.reduce((s, e) => s + e.quantity, 0);
  const pouchTotal = totalPouchPacks * POUCH_PRICE;
  const grandTotal = regularTotal + trashTotal + pouchTotal;
  const hasAnyItem = regularCans.length > 0 || trashCans.length > 0 || pouchEntries.length > 0;

  // ── Toggle handlers ──
  const toggleRegular = () => {
    if (regularOpen) {
      setRegularCans([]);
      setRegularOpen(false);
    } else {
      setRegularCans([newCanEntry('regular')]);
      setRegularOpen(true);
    }
  };

  const toggleTrash = () => {
    if (trashOpen) {
      setTrashCans([]);
      setTrashOpen(false);
    } else {
      setTrashCans([newCanEntry('trash')]);
      setTrashOpen(true);
    }
  };

  const togglePouches = () => {
    if (pouchesOpen) {
      setPouchEntries([]);
      setPouchesOpen(false);
    } else {
      setPouchEntries([newPouchEntry()]);
      setPouchesOpen(true);
    }
  };

  // ── Can entry handlers ──
  const updateCan = (
    setter: React.Dispatch<React.SetStateAction<CanEntry[]>>,
    id: string,
    field: keyof CanEntry,
    value: string
  ) => {
    setter((prev) => prev.map((e) => (e.id === id ? { ...e, [field]: value } : e)));
  };

  const removeCan = (
    setter: React.Dispatch<React.SetStateAction<CanEntry[]>>,
    setOpen: React.Dispatch<React.SetStateAction<boolean>>,
    id: string
  ) => {
    setter((prev) => {
      const next = prev.filter((e) => e.id !== id);
      if (next.length === 0) setOpen(false);
      return next;
    });
  };

  // ── Pouch entry handlers ──
  const updatePouch = (id: string, field: keyof PouchEntry, value: string | number) => {
    setPouchEntries((prev) => prev.map((e) => (e.id === id ? { ...e, [field]: value } : e)));
  };

  const removePouch = (id: string) => {
    setPouchEntries((prev) => {
      const next = prev.filter((e) => e.id !== id);
      if (next.length === 0) setPouchesOpen(false);
      return next;
    });
  };

  // ── Validation ──
  const validate = (): boolean => {
    const errs: FormErrors = {};
    if (!name.trim()) errs.name = 'Required';
    if (!phone.trim()) errs.phone = 'Required';
    if (!hasAnyItem) errs.atLeastOne = 'Please select at least one item to order';
    if (!logistics) errs.logistics = 'Required';

    if (pouchesOpen && pouchEntries.length > 0) {
      const pouchErrors: Record<string, { brand?: string; strength?: string }> = {};
      pouchEntries.forEach((entry) => {
        const entryErr: { brand?: string; strength?: string } = {};
        if (!entry.brand) entryErr.brand = 'Required';
        if (!entry.strength) entryErr.strength = 'Required';
        if (Object.keys(entryErr).length > 0) pouchErrors[entry.id] = entryErr;
      });
      if (Object.keys(pouchErrors).length > 0) errs.pouches = pouchErrors;
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    onSuccess({
      name,
      phone,
      regularCans,
      trashCans,
      pouchEntries,
      logistics,
      specialRequests,
      totalAmount: grandTotal,
    });
  };

  return (
    <section
      id="order"
      className="py-20 md:py-28 px-4 md:px-8"
      style={{
        background: '#1a1a1a',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='4' height='4'%3E%3Crect width='4' height='4' fill='%231a1a1a'/%3E%3Crect x='0' y='0' width='1' height='1' fill='%231e1e1e'/%3E%3Crect x='2' y='2' width='1' height='1' fill='%231e1e1e'/%3E%3C/svg%3E")`,
      }}
    >
      <div className="max-w-4xl mx-auto">
        <div className="mb-12">
          <p className="text-[#D4FF00] font-mono text-xs tracking-[0.4em] uppercase mb-3">Station Logbook</p>
          <h2
            className="text-4xl md:text-6xl text-white uppercase tracking-widest mb-4"
            style={{ fontFamily: '"Black Ops One", "Impact", sans-serif' }}
          >
            DISPATCH YOUR ORDER
          </h2>
          <div className="h-px max-w-xs bg-gradient-to-r from-[#CE1126] to-transparent mb-4" />
          <p className="font-mono text-[#888] text-sm">
            Complete station logbook below. Venmo payment required within 24 hours for order confirmation.
          </p>
        </div>

        <form id="zyn-order-form" data-readdy-form onSubmit={handleSubmit} className="space-y-8">

          {/* Name + Phone */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={labelClass}>Member FULL NAME<RequiredStar /></label>
              <input
                name="name"
                type="text"
                className={inputClass}
                placeholder="Full name..."
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              {errors.name && <p className="text-[#CE1126] font-mono text-xs mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className={labelClass}>Member PHONE NUMBER<RequiredStar /></label>
              <input
                name="phone"
                type="tel"
                className={inputClass}
                placeholder="(XXX) XXX-XXXX"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              {errors.phone && <p className="text-[#CE1126] font-mono text-xs mt-1">{errors.phone}</p>}
            </div>
          </div>

          {/* ── Item Selection ── */}
          <div>
            <label className={labelClass}>Order Items<RequiredStar /></label>
            <div className="space-y-3">

              {/* Regular Can toggle */}
              <div>
                <SectionToggle
                  active={regularOpen}
                  onToggle={toggleRegular}
                  icon="ri-archive-line"
                  label="Regular Can"
                  price="$50.00 / can"
                  accentColor="#CE1126"
                  count={regularCans.length}
                />
                {regularOpen && (
                  <div className="mt-3 space-y-3" style={{ animation: 'fadeSlideIn 0.25s ease' }}>
                    {regularCans.map((entry, i) => (
                      <CanEntryCard
                        key={entry.id}
                        entry={entry}
                        index={i}
                        canRemove={regularCans.length > 1}
                        onChange={(id, field, val) => updateCan(setRegularCans, id, field, val)}
                        onRemove={(id) => removeCan(setRegularCans, setRegularOpen, id)}
                      />
                    ))}
                    <button
                      type="button"
                      onClick={() => setRegularCans((prev) => [...prev, newCanEntry('regular')])}
                      className="w-full py-3 border border-dashed border-[#CE1126]/40 text-[#CE1126]/70 font-mono text-xs tracking-[0.3em] uppercase hover:border-[#CE1126] hover:text-[#CE1126] transition-all duration-200 cursor-pointer flex items-center justify-center gap-2"
                    >
                      <i className="ri-add-line text-base" />
                      Add Another Regular Can
                    </button>
                  </div>
                )}
              </div>

              {/* Trash Can toggle */}
              <div>
                <SectionToggle
                  active={trashOpen}
                  onToggle={toggleTrash}
                  icon="ri-stack-line"
                  label="Trash Can"
                  price="$75.00 / can"
                  accentColor="#C0C0C0"
                  count={trashCans.length}
                />
                {trashOpen && (
                  <div className="mt-3 space-y-3" style={{ animation: 'fadeSlideIn 0.25s ease' }}>
                    {trashCans.map((entry, i) => (
                      <CanEntryCard
                        key={entry.id}
                        entry={entry}
                        index={i}
                        canRemove={trashCans.length > 1}
                        onChange={(id, field, val) => updateCan(setTrashCans, id, field, val)}
                        onRemove={(id) => removeCan(setTrashCans, setTrashOpen, id)}
                      />
                    ))}
                    <button
                      type="button"
                      onClick={() => setTrashCans((prev) => [...prev, newCanEntry('trash')])}
                      className="w-full py-3 border border-dashed border-[#C0C0C0]/40 text-[#C0C0C0]/70 font-mono text-xs tracking-[0.3em] uppercase hover:border-[#C0C0C0] hover:text-[#C0C0C0] transition-all duration-200 cursor-pointer flex items-center justify-center gap-2"
                    >
                      <i className="ri-add-line text-base" />
                      Add Another Trash Can
                    </button>
                  </div>
                )}
              </div>

              {/* Pouches toggle */}
              <div>
                <SectionToggle
                  active={pouchesOpen}
                  onToggle={togglePouches}
                  icon="ri-capsule-line"
                  label="Pouches"
                  price="$7.50 / pack"
                  accentColor="#D4FF00"
                  count={totalPouchPacks > 0 ? totalPouchPacks : pouchEntries.length}
                />
                {pouchesOpen && (
                  <div className="mt-3 space-y-3" style={{ animation: 'fadeSlideIn 0.25s ease' }}>
                    <div className="flex items-center justify-between px-1">
                      <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#D4FF00]">
                        Pouch Specifications
                      </p>
                      <p className="font-mono text-xs text-[#555]">
                        {pouchEntries.length} flavor{pouchEntries.length !== 1 ? 's' : ''} · {totalPouchPacks} pack{totalPouchPacks !== 1 ? 's' : ''} total
                      </p>
                    </div>
                    {pouchEntries.map((entry, i) => (
                      <PouchEntryCard
                        key={entry.id}
                        entry={entry}
                        index={i}
                        canRemove={pouchEntries.length > 1}
                        onChange={updatePouch}
                        onRemove={removePouch}
                        error={errors.pouches?.[entry.id]}
                      />
                    ))}
                    <button
                      type="button"
                      onClick={() => setPouchEntries((prev) => [...prev, newPouchEntry()])}
                      className="w-full py-3 border border-dashed border-[#D4FF00]/40 text-[#D4FF00]/70 font-mono text-xs tracking-[0.3em] uppercase hover:border-[#D4FF00] hover:text-[#D4FF00] transition-all duration-200 cursor-pointer flex items-center justify-center gap-2"
                    >
                      <i className="ri-add-line text-base" />
                      Add Another Flavor
                    </button>
                  </div>
                )}
              </div>
            </div>
            {errors.atLeastOne && (
              <p className="text-[#CE1126] font-mono text-xs mt-2">{errors.atLeastOne}</p>
            )}
          </div>

          {/* Logistics */}
          <div>
            <label className={labelClass}>Logistics Method<RequiredStar /></label>
            <div className="flex gap-4">
              {['PICKUP', 'DELIVERY'].map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setLogistics(opt)}
                  className={`flex-1 py-5 border font-black text-base tracking-[0.3em] transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    logistics === opt
                      ? 'bg-[#CE1126] border-[#CE1126] text-white'
                      : 'bg-[#141414] border-[#3a3a3a] text-[#aaa] hover:border-[#CE1126]/60'
                  }`}
                  style={{ fontFamily: '"Black Ops One", "Impact", sans-serif' }}
                >
                  <i className={`${opt === 'PICKUP' ? 'ri-walk-line' : 'ri-truck-line'} mr-2`} />
                  {opt}
                </button>
              ))}
            </div>
            {errors.logistics && <p className="text-[#CE1126] font-mono text-xs mt-1">{errors.logistics}</p>}
            <input type="hidden" name="logistics" value={logistics} />
          </div>

          {/* Special Requests */}
          <div>
            <label className={labelClass}>Special Requests / Notes</label>
            <textarea
              name="specialRequests"
              className={`${inputClass} resize-none`}
              rows={4}
              placeholder="Additional instructions, delivery notes, custom requests..."
              value={specialRequests}
              maxLength={500}
              onChange={(e) => setSpecialRequests(e.target.value)}
            />
            <p className="text-[#555] font-mono text-xs mt-1 text-right">
              {specialRequests.length}/500
            </p>
          </div>

          {/* Running Total */}
          {hasAnyItem && (
            <div className="border border-[#CE1126]/40 bg-[#111] p-5" style={{ animation: 'fadeSlideIn 0.25s ease' }}>
              <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#888] mb-3">Order Total</p>
              <div className="space-y-2 font-mono text-sm mb-3">

                {regularCans.map((_, i) => (
                  <div key={`reg-${i}`} className="flex justify-between items-center">
                    <span className="text-[#888]">Regular Can #{i + 1}</span>
                    <span className="text-white">${REGULAR_CAN_PRICE.toFixed(2)}</span>
                  </div>
                ))}

                {trashCans.map((_, i) => (
                  <div key={`trash-${i}`} className="flex justify-between items-center">
                    <span className="text-[#888]">Trash Can #{i + 1}</span>
                    <span className="text-white">${TRASH_CAN_PRICE.toFixed(2)}</span>
                  </div>
                ))}

                {pouchEntries.map((entry, i) => (
                  <div key={entry.id} className="flex justify-between items-center">
                    <span className="text-[#888]">
                      Flavor #{i + 1}{entry.brand ? ` · ${entry.brand}` : ''}{entry.flavor ? ` ${entry.flavor}` : ''}{entry.strength ? ` ${entry.strength}` : ''} ×{entry.quantity}
                    </span>
                    <span className="text-[#D4FF00]">
                      ${(entry.quantity * POUCH_PRICE).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="h-px bg-[#2a2a2a] mb-3" />
              <div className="flex justify-between items-center">
                <span className="font-mono text-xs tracking-[0.25em] uppercase text-[#aaa]">Amount Due</span>
                <span
                  className="text-3xl font-bold text-white"
                  style={{ fontFamily: '"Courier New", monospace' }}
                >
                  ${grandTotal.toFixed(2)}
                </span>
              </div>
            </div>
          )}

          {/* Submit */}
          <div className="pt-4">
            <button
              type="submit"
              className="w-full py-6 bg-[#CE1126] border border-[#C0C0C0] text-white font-black uppercase tracking-[0.3em] text-xl transition-all duration-200 hover:-translate-y-1 active:scale-95 whitespace-nowrap cursor-pointer"
              style={{ fontFamily: '"Black Ops One", "Impact", sans-serif' }}
            >
              <span className="flex items-center justify-center gap-3">
                DISPATCH ORDER
                <i className="ri-send-plane-fill" />
              </span>
            </button>
          </div>
        </form>
      </div>

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
};

export default OrderForm;
